import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import StarRating from '../components/StarRating';
import { FileText, ShieldCheck, Users, Wallet, ExternalLink } from 'lucide-react';
import api, { formatApiErrorDetail } from '../lib/api';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

function ExpertCard({ expert, onVerify }) {
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const viewResume = async () => {
    try {
      const res = await api.get(`/admin/experts/${expert.id}/resume`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Could not load resume');
    }
  };

  return (
    <Card className="border border-gray-200" data-testid={`admin-expert-${expert.id}`}>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="font-bold text-[#003366]">{expert.fullName}</h3>
              <Badge className={
                expert.status === 'verified' ? 'bg-green-100 text-green-800' :
                expert.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
              } data-testid={`expert-status-${expert.id}`}>{expert.status}</Badge>
            </div>
            <p className="text-sm text-gray-600">{expert.email} • {expert.yearsExperience} yrs experience</p>
            <p className="text-sm text-gray-800 font-medium mt-1">{expert.headline}</p>
          </div>
          {expert.totalRatings > 0 && (
            <div className="text-right">
              <StarRating value={Math.round(expert.avgRating)} size={4} />
              <p className="text-xs text-gray-500">{expert.avgRating} ({expert.totalRatings} ratings)</p>
            </div>
          )}
        </div>
        <p className="text-sm text-gray-700 mb-2 line-clamp-3">{expert.bio}</p>
        <div className="flex flex-wrap gap-1 mb-3">
          {expert.skills.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s}</Badge>)}
        </div>
        {expert.certifications && <p className="text-xs text-gray-500 mb-1">Certifications: {expert.certifications}</p>}
        {expert.linkedin && (
          <a href={expert.linkedin} target="_blank" rel="noreferrer" className="text-xs text-[#0078D4] hover:underline flex items-center mb-3">
            <ExternalLink className="h-3 w-3 mr-1" /> LinkedIn
          </a>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <Button size="sm" variant="outline" onClick={viewResume} data-testid={`view-resume-${expert.id}`}>
            <FileText className="h-4 w-4 mr-1" /> View Resume
          </Button>
          {expert.status !== 'verified' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onVerify(expert.id, 'verified')} data-testid={`approve-expert-${expert.id}`}>
              Approve
            </Button>
          )}
          {expert.status === 'pending' && (
            <Button size="sm" variant="destructive" onClick={() => setShowReject(!showReject)} data-testid={`reject-expert-toggle-${expert.id}`}>
              Reject
            </Button>
          )}
        </div>
        {showReject && (
          <div className="mt-3 flex items-center space-x-2">
            <Input placeholder="Rejection reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`reject-reason-${expert.id}`} />
            <Button size="sm" variant="destructive" onClick={() => onVerify(expert.id, 'rejected', reason)} data-testid={`reject-expert-confirm-${expert.id}`}>
              Confirm
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminQueryCard({ query, onUpdate }) {
  const [response, setResponse] = useState(query.response || '');
  const [status, setStatus] = useState(query.status);
  const [saving, setSaving] = useState(false);

  return (
    <Card className="border border-gray-200" data-testid={`admin-query-${query.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-center flex-wrap gap-2 mb-2">
          <Badge className={query.status === 'Resolved' ? 'bg-green-100 text-green-800' : query.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}>{query.status}</Badge>
          <Badge variant="outline">{query.queryType}</Badge>
          <Badge className={query.isFree ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{query.isFree ? 'FREE' : `$${query.price}`}</Badge>
          {query.priority === 'Priority' && <Badge className="bg-red-100 text-red-800">Priority</Badge>}
          {query.expertName && <Badge className="bg-purple-100 text-purple-800">Expert: {query.expertName}</Badge>}
        </div>
        <p className="text-sm text-gray-500 mb-2">{query.name} ({query.email}){query.companyName ? ` — ${query.companyName}` : ''} • {new Date(query.submittedAt).toLocaleString()}</p>
        <p className="text-gray-700 text-sm whitespace-pre-wrap mb-3">{query.description}</p>
        {query.feedback && (
          <div className="bg-green-50 p-2 rounded mb-3 flex items-center space-x-2">
            <StarRating value={query.feedback.rating} size={4} />
            {query.feedback.comment && <p className="text-sm text-green-800 italic">"{query.feedback.comment}"</p>}
          </div>
        )}
        <div className="border-t pt-3 space-y-3">
          <Textarea value={response} onChange={(e) => setResponse(e.target.value)} placeholder="Expert response..." className="min-h-[80px]" data-testid={`admin-response-${query.id}`} />
          <div className="flex items-center space-x-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid={`admin-status-select-${query.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={saving}
              onClick={async () => { setSaving(true); await onUpdate(query.id, { status, response: response || null }); setSaving(false); }}
              className="bg-[#0078D4] hover:bg-[#005a9e]"
              data-testid={`admin-save-query-${query.id}`}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminPanel() {
  const [experts, setExperts] = useState([]);
  const [queries, setQueries] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  const loadAll = useCallback(async () => {
    const [e, q, w] = await Promise.all([
      api.get('/admin/experts'),
      api.get('/admin/queries'),
      api.get('/admin/withdrawals'),
    ]);
    setExperts(e.data);
    setQueries(q.data);
    setWithdrawals(w.data);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleVerify = async (id, status, rejectionReason) => {
    try {
      await api.patch(`/admin/experts/${id}`, { status, rejectionReason });
      toast.success(`Expert ${status}`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleQueryUpdate = async (id, body) => {
    try {
      await api.patch(`/admin/queries/${id}`, body);
      toast.success('Query updated');
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleWithdrawal = async (id, status) => {
    try {
      await api.patch(`/admin/withdrawals/${id}`, { status });
      toast.success(`Withdrawal ${status}`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const pendingExperts = experts.filter((e) => e.status === 'pending').length;
  const pendingWithdrawals = withdrawals.filter((w) => w.status === 'requested').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center space-x-3">
          <ShieldCheck className="h-8 w-8 text-[#0078D4]" />
          <div>
            <h1 className="text-3xl font-bold text-[#003366]" data-testid="admin-panel-title">Admin Panel</h1>
            <p className="text-gray-600">Manage experts, queries and withdrawals</p>
          </div>
        </div>

        <Tabs defaultValue="experts" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="experts" data-testid="admin-tab-experts">
              <Users className="h-4 w-4 mr-1" /> Experts ({pendingExperts} pending)
            </TabsTrigger>
            <TabsTrigger value="queries" data-testid="admin-tab-queries">Queries ({queries.length})</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals">
              <Wallet className="h-4 w-4 mr-1" /> Withdrawals ({pendingWithdrawals} pending)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="experts">
            {experts.length === 0 ? (
              <p className="text-center py-12 text-gray-500" data-testid="admin-experts-empty">No expert applications yet.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {experts.map((e) => <ExpertCard key={e.id} expert={e} onVerify={handleVerify} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="queries">
            {queries.length === 0 ? (
              <p className="text-center py-12 text-gray-500" data-testid="admin-queries-empty">No queries yet.</p>
            ) : (
              <div className="space-y-6">
                {queries.map((q) => <AdminQueryCard key={q.id} query={q} onUpdate={handleQueryUpdate} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="withdrawals">
            {withdrawals.length === 0 ? (
              <p className="text-center py-12 text-gray-500" data-testid="admin-withdrawals-empty">No withdrawal requests yet.</p>
            ) : (
              <div className="space-y-4">
                {withdrawals.map((w) => (
                  <Card key={w.id} className="border border-gray-200" data-testid={`admin-withdrawal-${w.id}`}>
                    <CardContent className="pt-6 flex flex-wrap justify-between items-center gap-4">
                      <div>
                        <p className="font-bold text-[#003366]">${w.amount.toFixed(2)} — {w.expertName}</p>
                        <p className="text-sm text-gray-600">{w.expertEmail}</p>
                        <p className="text-sm text-gray-600">
                          {w.bankName} • {w.accountName} • A/C {w.accountNumber}{w.ifscSwift ? ` • ${w.ifscSwift}` : ''}
                        </p>
                        <p className="text-xs text-gray-500">Requested {new Date(w.requestedAt).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={w.status === 'paid' ? 'bg-green-100 text-green-800' : w.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                          {w.status}
                        </Badge>
                        {w.status === 'requested' && (
                          <>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleWithdrawal(w.id, 'paid')} data-testid={`mark-paid-${w.id}`}>
                              Mark Paid
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleWithdrawal(w.id, 'rejected')} data-testid={`reject-withdrawal-${w.id}`}>
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
